import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, Zap } from 'lucide-react';

const About = () => {
    const stats = [
        { label: 'Founded', value: '2015' },
        { label: 'Projects', value: '500+' },
        { label: 'Team', value: '50+' },
        { label: 'Awards', value: '12' },
    ];

    return (
        <div className="about-container">
            <section className="about-hero-section">
                <div className="container">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="section-header"
                    >
                        <h1 className="section-title">Our Story</h1>
                        <p className="section-subtitle">Pioneering digital transformation for businesses worldwide.</p>
                    </motion.div>

                    <div className="about-layout">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="about-text-content"
                        >
                            <h2>Empowering Businesses Through Technology</h2>
                            <p>
                                At Aadhar Infotech, we believe that technology should be an enabler, not a hurdle. Founded in 2015, we've spent the last decade helping companies navigate the complex digital landscape with bespoke software solutions.
                            </p>
                            <p>
                                Our mission is simple: To deliver high-quality, scalable, and innovative digital products that drive growth and efficiency.
                            </p>

                            <div className="stats-grid">
                                {stats.map((stat, i) => (
                                    <div key={i} className="stat-item">
                                        <span className="stat-number gradient-text">{stat.value}</span>
                                        <span className="stat-text">{stat.label}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="about-image-wrapper"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1522071823991-b99c223004be?auto=format&fit=crop&q=80&w=800"
                                alt="Our Team Meeting"
                            />
                            <div className="experience-box glass-morphism">
                                <span className="exp-years">10+</span>
                                <span className="exp-label">Years of Excellence</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="values-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Our Core Values</h2>
                    </div>
                    <div className="values-grid">
                        <div className="value-card glass-morphism">
                            <div className="value-icon"><Users size={32} /></div>
                            <h3>Client-Centric</h3>
                            <p>Your success is our priority. We work as an extension of your team.</p>
                        </div>
                        <div className="value-card glass-morphism">
                            <div className="value-icon"><Zap size={32} /></div>
                            <h3>Agile Mindset</h3>
                            <p>We adapt and evolve rapidly to stay ahead of the curve.</p>
                        </div>
                        <div className="value-card glass-morphism">
                            <div className="value-icon"><Target size={32} /></div>
                            <h3>Quality First</h3>
                            <p>No compromises. We build robust systems that stand the test of time.</p>
                        </div>
                        <div className="value-card glass-morphism">
                            <div className="value-icon"><Award size={32} /></div>
                            <h3>Innovation</h3>
                            <p>We constantly explore new technologies to drive better results.</p>
                        </div>
                    </div>
                </div>
            </section>

            <style>{`
        .about-container { padding-top: 100px; }
        .about-hero-section { padding: 80px 0; }
        .section-header { text-align: center; margin-bottom: 5rem; }
        .section-title { font-size: 3.5rem; font-weight: 800; margin-bottom: 1rem; }
        .section-subtitle { font-size: 1.2rem; color: var(--text-muted); }
        
        .about-layout { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 6rem; align-items: center; margin-top: 4rem; }
        .about-text-content h2 { font-size: 2.5rem; margin-bottom: 2rem; line-height: 1.2; font-weight: 700; }
        .about-text-content p { font-size: 1.15rem; color: var(--text-muted); margin-bottom: 1.5rem; line-height: 1.8; }
        
        .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2.5rem; margin-top: 4rem; }
        .stat-number { font-size: 3rem; font-weight: 800; display: block; line-height: 1; margin-bottom: 0.5rem; }
        .stat-text { color: var(--text-muted); font-weight: 700; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 2px; }
        
        .about-image-wrapper { position: relative; }
        .about-image-wrapper img { width: 100%; border-radius: 2.5rem; border: 1px solid var(--border-glass); filter: brightness(0.9); }
        .experience-box { position: absolute; bottom: -30px; left: -30px; padding: 2.5rem; border-radius: 2rem; text-align: center; box-shadow: 0 20px 40px rgba(0,0,0,0.3); }
        .exp-years { display: block; font-size: 3rem; font-weight: 800; color: var(--primary); line-height: 1; }
        .exp-label { font-size: 0.9rem; font-weight: 700; color: #fff; text-transform: uppercase; }
        
        .values-section { padding: 120px 0; background-color: var(--bg-card); }
        .values-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 2rem; }
        .value-card { padding: 4rem 2rem; border-radius: 3rem; text-align: center; border: 1px solid var(--border-glass); transition: var(--transition); }
        .value-card:hover { transform: translateY(-15px); border-color: var(--primary); background: rgba(255,255,255,0.08); }
        .value-icon { margin-bottom: 2rem; color: var(--primary); transition: var(--transition); }
        .value-card:hover .value-icon { transform: scale(1.2); }
        .value-card h3 { font-size: 1.6rem; margin-bottom: 1.2rem; font-weight: 700; }
        .value-card p { color: var(--text-muted); line-height: 1.7; }

        @media (max-width: 1024px) {
          .about-layout { gap: 3rem; }
          .section-title { font-size: 3rem; }
        }

        @media (max-width: 768px) {
          .about-hero-section { padding: 60px 0; }
          .about-layout { grid-template-columns: 1fr; text-align: center; gap: 5rem; }
          .about-text-content h2 { font-size: 2rem; }
          .stats-grid { gap: 2rem; }
          .experience-box { left: 50%; transform: translateX(-50%); bottom: -40px; width: 220px; }
          .values-section { padding: 80px 0; }
          .value-card { padding: 3rem 1.5rem; }
        }
      `}</style>
        </div>
    );
};

export default About;
